import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, X, Trash2 } from 'lucide-react';
import AdBanner from './AdBanner';

const STORAGE_KEY = 'kalonews_recent';
const MAX_ITEMS = 10;

function readRecent() {
  try {
    const arr = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(arr) ? arr.slice(0, MAX_ITEMS) : [];
  } catch {
    return [];
  }
}

// Desktop only — floating icon (DRAGGABLE by holding), right-to-left slide sidebar
// Click gareko news haru localStorage ma 10 ota samma store hunchha
function RecentNewsDrawer() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(readRecent);
  const [confirmClear, setConfirmClear] = useState(false);

  // Draggable icon position — default: scroll-to-top arrow ko mathi
  const [pos, setPos] = useState(null); // null = default CSS position
  const dragRef = useRef({ active: false, moved: false, startDist: 0, offX: 0, offY: 0 });
  const lastDragEnd = useRef(0);

  useEffect(() => {
    const handler = () => setItems(readRecent());
    window.addEventListener('kalonews:recent-updated', handler);
    return () => window.removeEventListener('kalonews:recent-updated', handler);
  }, []);

  const onPointerDown = useCallback((e) => {
    const btn = e.currentTarget.getBoundingClientRect();
    const cx = btn.left + btn.width / 2;
    const cy = btn.top + btn.height / 2;
    dragRef.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      offX: e.clientX - cx,
      offY: e.clientY - cy,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  }, []);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;

    // 4px bhanda badi saryo bhane matra "drag" manne — natra click nai hunchha
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 4) return;
    d.moved = true;

    const btn = e.currentTarget.getBoundingClientRect();
    const x = Math.min(window.innerWidth - btn.width / 2 - 8, Math.max(btn.width / 2 + 8, e.clientX - d.offX));
    const y = Math.min(window.innerHeight - btn.height / 2 - 8, Math.max(btn.height / 2 + 8, e.clientY - d.offY));
    setPos({ x, y });
  }, []);

  const onPointerUp = useCallback(() => {
    const d = dragRef.current;
    if (d.active && d.moved) lastDragEnd.current = Date.now();
    d.active = false;
  }, []);

  const handleClick = () => {
    // Drag bhayeko ho bhane click ignore (toggle huna hudaina)
    if (Date.now() - lastDragEnd.current < 250) return;
    setOpen((o) => !o);
  };

  const clearAll = () => {
    localStorage.removeItem(STORAGE_KEY);
    setItems([]);
    setConfirmClear(false);
  };

  const openArticle = (item) => {
    setOpen(false);
    navigate(`/article/${item.id}`);
  };

  // Default thau: ScrollTopButton (bottom-6 right-6, w-12) ko tessa mathi
  const iconStyle = pos ? { left: pos.x, top: pos.y } : { bottom: '6.5rem', right: '1.5rem' };

  return (
    <div className="hidden lg:block" style={{ zIndex: 45 }}>
      {/* Floating DRAGGABLE icon */}
      <button
        type="button"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={handleClick}
        aria-label="हालै हेरेका समाचार"
        title="हालै हेरेका समाचार (समातेर तान्न सकिन्छ)"
        className={`fixed w-12 h-12 rounded-full bg-crimson text-white shadow-xl flex items-center justify-center transition-colors duration-200 hover:bg-crimson-dark cursor-grab active:cursor-grabbing touch-none select-none ${
          open ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={iconStyle}
      >
        <History size={22} className="pointer-events-none" />
        {items.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-ink text-[10px] font-bold flex items-center justify-center pointer-events-none">
            {items.length}
          </span>
        )}
      </button>

      {/* Outside click backdrop — drawer बाहिर थिच्दा बन्द हुन्छ */}
      {open && (
        <div
          className="fixed inset-0 bg-black/25"
          style={{ zIndex: 45 }}
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Slide panel — right to left */}
      <aside
        className={`fixed right-0 top-1/2 -translate-y-1/2 w-[320px] max-w-[88vw] transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ maxHeight: '78vh', zIndex: 46 }}
        aria-hidden={!open}
      >
        <div className="bg-white border border-line rounded-l-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '78vh' }}>
          {/* Header + delete all */}
          <div className="flex items-center justify-between gap-2 px-4 py-3.5 bg-gradient-to-r from-crimson to-crimson-dark text-white flex-shrink-0">
            <span className="flex items-center gap-2 font-display font-bold text-[14.5px]">
              <History size={16} /> हालै हेरेका समाचार
            </span>
            <div className="flex items-center gap-1">
              {items.length > 0 && (
                <button type="button" onClick={() => setConfirmClear(true)} aria-label="इतिहास मेट्नुहोस्" title="सबै मेट्नुहोस्" className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                  <Trash2 size={15} />
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} aria-label="बन्द गर्नुहोस्" className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                <X size={17} />
              </button>
            </div>
          </div>

          {/* List — bhitra scroll garda pachadi page scroll hudaina (overscroll-contain + hidden scrollbar) */}
          <div className="overflow-y-auto flex-1 overscroll-contain no-scrollbar">
            {items.length === 0 ? (
              <p className="text-[12.5px] text-muted text-center py-8 px-4">अझै कुनै समाचार हेरिएको छैन। कुनै समाचार खोल्नुभयो भने यहाँ देखिन्छ।</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openArticle(n)}
                  className="w-full flex items-start gap-3 px-4 py-3 border-b border-line last:border-b-0 text-left hover:bg-paper-soft transition-colors group"
                >
                  <img
                    src={n.image || 'https://via.placeholder.com/56x56?text=K'}
                    alt=""
                    className="w-11 h-11 rounded-lg object-cover bg-paper-tint flex-shrink-0"
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[12.5px] font-semibold text-ink leading-snug line-clamp-2 group-hover:text-blue transition-colors">
                      {n.title}
                    </span>
                    {n.ts && (
                      <span className="block text-[10.5px] text-muted mt-0.5">
                        {new Date(n.ts).toLocaleDateString('ne-NP')} • {new Date(n.ts).toLocaleTimeString('ne-NP', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </span>
                </button>
              ))
            )}
          </div>

          {/* हालै हेरेका समाचार — mini ad */}
          {items.length > 0 && (
            <div className="flex-shrink-0 border-t border-line px-3 py-2.5">
              <AdBanner position="recent_mini" maxAds={1} showAll={false} />
            </div>
          )}
        </div>
      </aside>

      {/* Delete confirm popup modal */}
      {confirmClear && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 60 }} onClick={() => setConfirmClear(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 pt-6 pb-4 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 text-crimson flex items-center justify-center">
                <Trash2 size={22} />
              </div>
              <h4 className="font-display text-lg font-bold mb-1.5">इतिहास मेट्नुहुन्छ?</h4>
              <p className="text-[13px] text-muted leading-relaxed">हालै हेरेका सबै ({items.length}) समाचारहरू मेटिनेछन्। यो कार्य फिर्ता हुन सक्दैन।</p>
            </div>
            <div className="flex border-t border-line">
              <button type="button" onClick={() => setConfirmClear(false)} className="flex-1 py-3.5 text-[13.5px] font-bold text-ink-soft hover:bg-paper-soft transition-colors">
                रद्द
              </button>
              <button type="button" onClick={clearAll} className="flex-1 py-3.5 text-[13.5px] font-bold text-white bg-crimson hover:bg-crimson-dark transition-colors">
                मेट्नुहोस्
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecentNewsDrawer;